import { Component, OnInit } from '@angular/core';
import { TIME_TRACKING_TIME_FORMAT } from '@app/constants/time-and-date-format-strings';
import { FeatureFlag } from '@app/enums';
import { DeadlineReminder } from '@app/modules/time-tracking/interfaces/deadline-reminders.interface';
import { Settings } from '@app/modules/time-tracking/interfaces/settings.interface';
import { NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Locale } from '@app/types/translatable.type';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { TimeTrackingSettingsService } from '../../services/time-tracking-settings.service';

@Component({
    selector: 'app-time-tracking-general-settings-card',
    templateUrl: './general-settings-card.component.html',
    styleUrls: ['./general-settings-card.style.scss'],
})
export class GeneralSettingsCardComponent implements OnInit {
    isLoading = false;
    anchorDate = moment();
    selectedDisplayTime = '';
    setupWeeklyDeadline = false;
    settings: Settings = {
        remindersWeeklyDeadlineDayOfWeek: null,
    };

    timeZone = 'ET';

    approverReminders: Omit<DeadlineReminder, 'fieldId'>[] = [];
    employeeReminders: Omit<DeadlineReminder, 'fieldId'>[] = [];
    hasTimeClockFlag = false;
    hasTimerClockFlag = false;

    currentLocale = this.translateService.currentLang as Locale;

    constructor(
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private featureService: FeatureService,
        private notifyService: NotifyService,
        private translateService: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        this.hasTimeClockFlag = await this.featureService.has(FeatureFlag.timeClock);
        this.hasTimerClockFlag = await this.featureService.has(FeatureFlag.timeClockTimer);

        this.isLoading = true;

        try {
            this.settings = await this.timeTrackingSettingsService.get();

            this.setupWeeklyDeadline = this.settings.remindersWeeklyDeadline ?? false;

            this.selectedDisplayTime = moment(this.settings.remindersWeeklyDeadlineTimeOfDay, 'HH:mm').format(
                TIME_TRACKING_TIME_FORMAT[this.currentLocale]
            );

            if (this.settings.remindersWeeklyDeadlineDayOfWeek) {
                // Create a moment object with the day of the weekly reminders
                this.anchorDate = moment().day(this.settings.remindersWeeklyDeadlineDayOfWeek);
            }

            this.setupApproverReminders();
            this.setupEmployeeReminders();
        } catch (err) {
            this.notifyService.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    setupApproverReminders(): void {
        if (
            this.settings.remindersApproversDeadline1 !== undefined ||
            this.settings.remindersApproversDeadline1 !== null
        ) {
            this.approverReminders.push({
                deadline: this.settings.remindersApproversDeadline1,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersApproversDeadline1, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }

        if (this.settings.remindersApproversDeadline2 !== undefined) {
            this.approverReminders.push({
                deadline: this.settings.remindersApproversDeadline2,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersApproversDeadline2, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }
        if (this.settings.remindersApproversDeadline3 !== undefined) {
            this.approverReminders.push({
                deadline: this.settings.remindersApproversDeadline3,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersApproversDeadline3, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }
    }

    setupEmployeeReminders(): void {
        if (
            this.settings.remindersEmployeesDeadline1 !== undefined ||
            this.settings.remindersEmployeesDeadline1 !== null
        ) {
            this.employeeReminders.push({
                deadline: this.settings.remindersEmployeesDeadline1,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersEmployeesDeadline1, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }

        if (this.settings.remindersEmployeesDeadline2 !== undefined) {
            this.employeeReminders.push({
                deadline: this.settings.remindersEmployeesDeadline2,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersEmployeesDeadline2, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }

        if (this.settings.remindersEmployeesDeadline3 !== undefined) {
            this.employeeReminders.push({
                deadline: this.settings.remindersEmployeesDeadline3,
                dayOfWeek: this.anchorDate
                    .clone()
                    .subtract(this.settings.remindersEmployeesDeadline3, 'days')
                    .format('dddd')
                    .toLowerCase(),
            });
        }
    }
}
